import {computed, defineComponent, inject, nextTick, onMounted, reactive, ref, watch,} from "vue";

import createSearchItem from "./components/SearchItem.jsx";

import "./index.less";
import {NEWBIE_SEARCH} from "../provider/NewbieProvider.jsx";
import {Button, Modal, Space, Tag, Tooltip} from "ant-design-vue";
import {cloneDeep, concat, find, isArray, isBoolean, isFunction, isString, isUndefined, orderBy,} from "lodash-es";
import {createExpand, createSortable} from "./components";
import {
	ArrowsAltOutlined,
	ClearOutlined,
	FallOutlined,
	HistoryOutlined,
	OrderedListOutlined,
	RiseOutlined,
	SearchOutlined,
	ShrinkOutlined,
} from "@ant-design/icons-vue";
import {parsePersistenceConfig, usePersistence, useSm3, useT} from "../../hooks";
import dayjs from "dayjs";

/**
 * 搜索组件
 * @version 1.0.0
 */
export default defineComponent({
	name: "NewbieSearch",
	props: {
		/**
		 * @typedef {Object} SearchItemConfig 搜索项
		 * @property {string} key 搜索项的 key
		 * @property {string} title 搜索项的标题
		 * @property {string} type 搜索项的类型
		 * @property {number} sortOrder 排序，数字越大越靠前，默认为0
		 * @property {boolean|Object} [expandable] 搜索项是否展开
		 * @property {Array|Function} [options] 搜索项的选项
		 * @property {Object} [inputProps] 搜索项的输入框属性
		 * @property {boolean} [disableConditions] 是否禁用搜索条件
		 * @property {Array} [conditions] 搜索项的条件，可选项为不同类型的内置条件
		 * @property {*} [defaultValue] 默认搜索值
		 * @property {*} [defaultCondition] 默认搜索条件
		 * @property {Function} [customRender] 自定义渲染搜索项， 接收 { item, queryForm, context } 作为参数，返回一个对象，包含 conditions, Component, displayValue, onComponentOpen 四个属性
		 * @property {Function} [collectItem] 收集搜索项的内容]
		 * */

		/**
		 * 搜索项的配置, 详见 [SearchItemConfig](#searchitemconfig-配置)
		 */
		filterableColumns: { type: [Array, Function], default: () => [] },

		/**
		 * 可排序的搜索项
		 */
		sortableColumns: { type: [Array, Function], default: () => [] },

		/**
		 * 是否禁用搜索条件
		 */
		disableConditions: { type: Boolean, default: false },

		/**
		 * 是否在搜索条件变化时自动搜索
		 */
		autoQuery: { type: Boolean, default: false },

		/**
		 * 搜索项的间距
		 */
		gutter: { type: [String, Number], default: 16 },

		/**
		 * 持久化，传入 localStorage 的 key，如果为 true, 将会以 URL Hash 为 key
		 */
		persistence: { type: [Boolean, String, Object], default: false },
	},
	emits: [
		/**
		 * 点击搜索
		 * @property queryForm {Object} 搜索栏的值
		 */
		"search",
	],
	setup(props, { expose, emit, slots }) {
		const searchProvider = inject(NEWBIE_SEARCH, () => {});

		/********** 持久化配置 **********/

		const persistenceConf = computed(() => parsePersistenceConfig(props.persistence));
		const enableLocalStorage = computed(() => persistenceConf.value.storage === "local");

		const persistenceNamespace = computed(() => {
			if (!persistenceConf.value.enabled) return null;
			const { key } = persistenceConf.value;
			if (!key) return `search_${useSm3(location.href)}`;
			return `search_${useSm3(location.pathname + "_" + key)}`;
		});

		const persist = computed(() => {
			const ns = persistenceNamespace.value;
			if (!ns) return null;
			return usePersistence(ns, { storage: persistenceConf.value.storage });
		});

		/********** 状态 **********/

		const state = reactive({
			queryForm: {}, // 搜索表单
			sortForm: { selectedKeys: [], targetKeys: [] }, // 排序表单

			columnKey: {}, // 自定义列
			searchKey: {}, // 自定义搜索框
			searchLabels: [], // 搜索项展示

			fieldColumns: [], //表单搜索项
			expandColumns: [], //展开搜索项
			sortColumns: [], //可排序搜索项

			openSortable: false, //是否打开排序设置
			isFormExpanded: false, //是否已经展开
			overflowCount: 0, //折叠时隐藏的搜索项数量
		});

		const formWrapperRef = ref();

		//带有 between 的搜索项则初始化为 [null, null]
		const betweenKeys = ["number", "date"];

		//可以多选的的搜索项则初始化为 []
		const arrayKeys = ["select", "cascade"];

		const searchState = {}; // 用于记录各个搜索项的状态

		/**
		 * 初始化搜索
		 */
		const init = () => {
			const form = {};
			const expandColumns = [];
			const fieldColumns = [];
			const sortColumns = [];
			const defaultSortKeys = [];

			// localStorage 模式不加载冷数据，默认值始终优先；冷数据只在用户点"应用"时使用
			const persistenceSearchData = persist.value && !enableLocalStorage.value ? persist.value.load("form", {}) : {};
			const persistenceSortData = persist.value && !enableLocalStorage.value ? persist.value.load("sort", {}) : {};

			props.filterableColumns.forEach(column => {
				const item = cloneDeep(column);
				if (isUndefined(item.sortOrder)) {
					item.sortOrder = 0;
				}

				if (!searchState[item.key]) {
					searchState[item.key] = reactive({
						showPanel: false,
					});
				}

				item.type = item.type || "input";
				let value = "",
					condition = "equal";
				//如果是展开显示的，那么默认值为空字符串
				//如果展开为多选，那么默认值为 []
				if (item.expandable) {
					value = [];
				} else if (betweenKeys.includes(item.type)) {
					value = [null, null];
				} else if (arrayKeys.includes(item.type)) {
					value = [];
				}

				//默认值处理
				if (!isUndefined(persistenceSearchData[item.key])) {
					const persistenceValue = persistenceSearchData[item.key].value;
					if (
						(betweenKeys.includes(item.type) || arrayKeys.includes(item.type)) &&
						!isArray(persistenceValue)
					) {
						value = [persistenceValue];
					} else {
						value = persistenceValue;
					}
				} else if (!isUndefined(item.defaultValue)) {
					if (isFunction(item.defaultValue)) {
						value = item.defaultValue();
					} else {
						value = item.defaultValue;
					}
				}

				//默认搜索条件
				if (!isUndefined(persistenceSearchData[item.key])) {
					condition = persistenceSearchData[item.key].condition;
				} else if (item.conditions && item.conditions.length === 1) {
					condition = item.conditions[0];
				} else if (item.defaultCondition) {
					if (isFunction(item.defaultCondition)) {
						condition = item.defaultCondition();
					} else {
						condition = item.defaultCondition;
					}
				}

				if (item.expandable) {
					if (!item.options) {
						console.error(`expandable 为 true 时，必须提供 options 属性`);
						return;
					}
					item.type = "select";

					expandColumns.push(item);
				} else {
					if (isUndefined(searchState[item.key].hidden)) {
						searchState[item.key].hidden = false;
					}
					fieldColumns.push(item);
				}

				if (item.type === "date") {
					if (isArray(value)) {
						value = value.map(v => (isString(v) ? dayjs(new Date(v)) : v));
					} else if (isString(value)) {
						value = dayjs(new Date(value));
					}
				}

				if (item.type === "switch") {
					value = isBoolean(value) ? (value ? "checked" : "unchecked") : undefined;
				}

				form[item.key] = {
					value,
					type: item.type,
					condition,
				};
			});

			props.sortableColumns.forEach(column => {
				const item = cloneDeep(column);

				if (!isUndefined(persistenceSortData[column.key])) {
					defaultSortKeys.push(column.key);
					item.direction = persistenceSortData[column.key];
				}

				if (column.direction === "asc" || column.direction === "desc") {
					defaultSortKeys.push(column.key);
				}
				sortColumns.push(item);
			});

			state.queryForm = form;
			state.sortColumns = orderBy(sortColumns, ["sortOrder"], ["desc"]);
			state.fieldColumns = orderBy(fieldColumns, ["sortOrder"], ["desc"]);
			state.expandColumns = orderBy(expandColumns, ["sortOrder"], ["desc"]);
			state.sortForm.targetKeys = defaultSortKeys;
		};

		watch(
			() => [props.filterableColumns, props.sortableColumns],
			() => {
				init();
				nextTick(() => updateOverflow());
			},
			{ immediate: true }
		);

		const onSearch = () => {
			state.openSortable = false;
			showRestore.value = false;
			emit("search", { newbieQuery: getQueryForm(), newbieSort: getSortForm() });
		};

		const onClear = () => {
			if (persist.value) persist.value.clear(["search", "sort"]);
			showRestore.value = false;
			init();
		};

		const onToggleMore = () => {
			state.isFormExpanded = !state.isFormExpanded;
			nextTick(() => updateOverflow());
		};

		/********** 折叠逻辑（基于容器宽度测量） **********/

		const updateOverflow = () => {
			if (!formWrapperRef.value) return;
			const container = formWrapperRef.value;
			const firstItem = container.querySelector(".newbie-search-item");

			let perRow = state.fieldColumns.length; // 默认：全部可见

			if (firstItem && state.fieldColumns.length > 0) {
				const itemWidth = firstItem.getBoundingClientRect().width;
				if (itemWidth > 0) {
					const containerWidth = container.getBoundingClientRect().width;
					const gap = Number(props.gutter) || 0;
					perRow = Math.max(1, Math.floor((containerWidth + gap) / (itemWidth + gap)));
				}
			}

			// 折叠时只显示 perRow 个，展开时显示全部
			const visibleCount = state.isFormExpanded ? state.fieldColumns.length : perRow;
			state.overflowCount = state.fieldColumns.length - visibleCount;

			// 逐条设置 item 的 hidden 状态
			// 注意：如果某个 item 的面板正在显示中，则不隐藏它，避免下拉面板被裁剪
			state.fieldColumns.forEach((item, index) => {
				const shouldHide = index >= visibleCount;
				// 如果面板正在显示，强制保持可见
				if (shouldHide && searchState[item.key]?.showPanel) {
					searchState[item.key].hidden = false;
				} else {
					searchState[item.key].hidden = shouldHide;
				}
			});
		};

		onMounted(() => {
			if (persist.value && !enableLocalStorage.value) {
				// 内存模式：自动恢复上次搜索条件（同 SPA 导航）
				emit("search", {
					persistence: true,
					newbieQuery: getQueryForm(),
					newbieSort: getSortForm(),
				});
			} else if (persist.value && enableLocalStorage.value) {
				if (persist.value.hasLocalData("search")) {
					// 冷存储模式：如果有上次保存的条件，显示恢复入口
					showRestore.value = true;
				}
				// 默认条件优先：发出带默认值的搜索，但不保存到持久化（避免覆盖冷数据）
				emit("search", {
					persistence: true,
					newbieQuery: getQueryForm(true),
					newbieSort: getSortForm(true),
				});
			} else if (props.persistence) {
				// 异常兜底：persistence prop 已配置但实例不可用（理论上不应进入此分支）
				// 发出空搜索确保 Table 能获取初始数据
				emit("search", {
					persistence: true,
					newbieQuery: {},
					newbieSort: {},
				});
			}
			// 初始测量（等待 DOM 就绪）
			requestAnimationFrame(() => updateOverflow());
		});

		/********** exposes **********/

		const getSortForm = (skipSave = false) => {
			const form = {};

			state.sortForm.targetKeys.forEach(key => {
				const item = find(state.sortColumns, { key });
				form[key] = item.direction;
				const sortLabelIdx = state.searchLabels.length;
				state.searchLabels.push(
					<Tag
						color={"green"}
						closable
						onClose={e => {
							e?.preventDefault?.();
							const sidx = state.sortForm.targetKeys.indexOf(key);
							if (sidx > -1) state.sortForm.targetKeys.splice(sidx, 1);
							state.searchLabels[sortLabelIdx] = null;
						}}
					>
						{() => [
							<span style={{ marginRight: "2px" }}>{item.title}</span>,
							item.direction === "asc" ? <RiseOutlined /> : <FallOutlined />,
						]}
					</Tag>
				);
			});

			if (persist.value && !skipSave) {
				persist.value.save("sort", form);
			}
			return form;
		};

		/**
		 * 获取表单实时数据
		 * @return {*}
		 */
		const getQueryForm = (skipSave = false) => {
			const form = {};
			const persistenceForm = {}; //由于 Form 里的值会按 Provider 进行格式化，但这里需要的是原始值，所以记录多一次
			state.searchLabels = [];

			concat(state.fieldColumns, state.expandColumns).forEach(item => {
				let { value, searchLabel } = item.collectItem
					? item.collectItem({ queryForm: state.queryForm, item })
					: {};
				const { condition, type } = state.queryForm[item.key];

				if (
					(Array.isArray(value) && value.length > 0) ||
					(!Array.isArray(value) &&
						value !== null &&
						value !== undefined &&
						value !== "") ||
					condition === "null" ||
					condition === "notNull"
				) {
					if (props.persistence) {
						persistenceForm[item.key] = { condition, type, value };
					}
					//对返回值做预处理
					const formatter = searchProvider?.valueFormatter?.[item.type];
					if (formatter && isFunction(formatter)) {
						value = isArray(value) ? value.map(v => formatter(v)) : formatter(value);
					}
					form[item.key] = { condition, type, value };
					const labelIdx = state.searchLabels.length;
					state.searchLabels.push(
						searchLabel ? (
							<Tag
								color={"blue"}
								closable
								onClose={e => {
									e?.preventDefault?.();
									if (state.queryForm[item.key]) {
										const t = state.queryForm[item.key].type || item.type;
										if (betweenKeys.includes(t)) {
											state.queryForm[item.key].value = [null, null];
										} else if (arrayKeys.includes(t)) {
											state.queryForm[item.key].value = [];
										} else if (t === "switch") {
											state.queryForm[item.key].value = undefined;
										} else {
											state.queryForm[item.key].value = "";
										}
									}
									state.searchLabels[labelIdx] = null;
								}}
							>
								{searchLabel}
							</Tag>
						) : null
					);
				}
			});

			if (persist.value && !skipSave) {
				persist.value.save("search", persistenceForm);
			}

			return form;
		};

		/**
		 * 设置搜索项的值
		 * @param {Object} fields
		 */
		const setQueryForm = fields => {
			Object.keys(fields).forEach(key => {
				if (state.queryForm[key]) {
					state.queryForm[key].value = fields[key];
				} else {
					state.queryForm[key] = {
						value: fields[key],
					};
				}
			});
		};

		const getSearch = () => {
			state.searchLabels = [];

			const searchForm = {};
			const newbieQuery = getQueryForm();
			const newbieSort = getSortForm();

			if (Object.keys(newbieQuery).length) {
				searchForm.newbieQuery = newbieQuery;
			}

			if (Object.keys(newbieSort).length) {
				searchForm.newbieSort = newbieSort;
			}
			return searchForm;
		};

		expose({ getQueryForm, setQueryForm, getSortForm, getSearch });

		/********** 冷恢复逻辑（localStorage 模式） **********/

		const showRestore = ref(false);

		const onApplyRestore = () => {
			if (!persist.value) return;

			// 从 localStorage 加载冷数据
			const coldForm = persist.value.loadLocal("search", {});
			const coldSort = persist.value.loadLocal("sort", {});

			// 同步到内存
			persist.value.save("search", coldForm);
			persist.value.save("sort", coldSort);

			// 应用到表单 state
			Object.keys(coldForm).forEach(key => {
				const item = coldForm[key];
				let value = item.value;
				// select/cascade/date 类型的值必须是数组
				const field = state.queryForm[key];
				const type = field?.type || item.type || "input";
				if (
					(betweenKeys.includes(type) || arrayKeys.includes(type)) &&
					!isArray(value) &&
					value != null &&
					value !== ""
				) {
					value = [value];
				}

				if (field) {
					field.value = value;
					if (item.condition) field.condition = item.condition;
				} else {
					state.queryForm[key] = {
						value,
						condition: item.condition || "equal",
						type,
					};
				}
			});

			// 类型转换：恢复后的数据需要经过 init() 中的类型处理
			// date 字段：ISO 字符串 → dayjs 对象
			// switch 字段：布尔值 → "checked"/"unchecked"
			Object.keys(state.queryForm).forEach(key => {
				const field = state.queryForm[key];
				if (!field || !field.type) return;
				if (field.type === "date" && field.value != null) {
					field.value = isArray(field.value)
						? field.value.map(v => (isString(v) ? dayjs(new Date(v)) : v))
						: isString(field.value)
							? dayjs(new Date(field.value))
							: field.value;
				} else if (field.type === "switch" && field.value != null) {
					field.value = isBoolean(field.value)
						? field.value
							? "checked"
							: "unchecked"
						: undefined;
				}
			});

			// 应用冷排序数据到 sortForm
			const sortTargetKeys = [];
			Object.keys(coldSort).forEach(key => {
				sortTargetKeys.push(key);
				const sortItem = find(state.sortColumns, { key });
				if (sortItem) {
					sortItem.direction = coldSort[key];
				}
			});
			state.sortForm.targetKeys = sortTargetKeys;

			showRestore.value = false;
			onSearch();
		};

		/********** render **********/

		const searchElems = () => {
			return state.fieldColumns.map(item =>
				createSearchItem(item, state.queryForm, searchState[item.key], {
					props,
					slots,
					searchProvider,
				})
			);
		};

		const expandElems = () =>
			state.expandColumns.map(item => createExpand(item, state.queryForm));

		const sortableElem = () =>
			state.sortColumns.length ? (
				<Modal
					title={useT("search.sort-setting")}
					v-model:open={state.openSortable}
					width={"800px"}
					okText={useT("common.search")}
					cancelText={useT("common.cancel")}
					onOk={() => onSearch()}
				>
					{() => createSortable(state.sortColumns, state.sortForm)}
				</Modal>
			) : null;

		return () => {
			return (
				<div class={"newbie-search"}>
					<div class={"newbie-search-query"}>
						<div class={"newbie-search-form"}>
							<div class={`newbie-search-form-wrapper`} ref={formWrapperRef}>
								<Space
									size={[props.gutter, 10]}
									wrap
									style={{ marginBottom: 0 }}
								>
									{() => searchElems()}
								</Space>
							</div>
						</div>

						<Space class={"newbie-search-operation"}>
							{() => [
								state.overflowCount > 0 || state.isFormExpanded ? (
									<Tooltip title={useT("search.toggle-more")}>
										{() => (
											<Button
												type={"link"}
												icon={
													state.isFormExpanded ? (
														<ShrinkOutlined />
													) : (
														<ArrowsAltOutlined />
													)
												}
												onClick={onToggleMore}
											>
												{() =>
													state.isFormExpanded
														? useT("search.collapse")
														: useT("common.more") +
															"(" +
															state.overflowCount +
															")"
												}
											</Button>
										)}
									</Tooltip>
								) : null,
								<Tooltip title={useT("search.clear-all-search-items")}>
									{() => (
										<Button
											type={"dashed"}
											icon={<ClearOutlined />}
											onClick={onClear}
										>
											{() =>
												props.sortableColumns.length
													? ""
													: useT("common.clear")
											}
										</Button>
									)}
								</Tooltip>,
								props.sortableColumns.length ? (
									<Tooltip title={useT("search.sort-setting")}>
										{() => (
											<Button
												type={
													state.sortForm.targetKeys.length
														? "primary"
														: "default"
												}
												icon={<OrderedListOutlined />}
												onClick={() => (state.openSortable = true)}
											></Button>
										)}
									</Tooltip>
								) : null,
								<Button
									type={"primary"}
									icon={<SearchOutlined />}
									onClick={onSearch}
								>
									{() => useT("common.search")}
								</Button>,
							]}
						</Space>
					</div>
					{state.expandColumns?.length ? (
						<div class={"newbie-search-expand"}>{expandElems()}</div>
					) : null}
					{state.searchLabels?.length || showRestore.value ? (
						<div class={"newbie-search-label"}>
							<Space
								class={"newbie-search-label-content"}
								wrap={true}
								style={{ marginBottom: 0 }}
							>
								{() => [
									showRestore.value ? (
										<Tag
											style={{
												cursor: "pointer",
												color: "#92400e",
												background: "#fff7e6",
												borderColor: "#ffa940",
												borderStyle: "dashed",
											}}
											onClick={onApplyRestore}
										>
											{() => [
												<HistoryOutlined style={{ marginRight: "4px" }} />,
												useT("search.restore-last"),
											]}
										</Tag>
									) : null,
									...state.searchLabels.filter(Boolean),
								]}
							</Space>
						</div>
					) : null}
					{sortableElem()}
				</div>
			);
		};
	},
});
